// ---------------------------------------------------------------------------
// Notification display helpers — single source of wording + routing for a
// notification. Pure functions over a NotificationView (no store/reactivity),
// so they can be reused (drawer, bell dropdown, toasts) and unit-tested
// standalone. The backend `type` is dotted ("bug.assigned"): the prefix is the
// entity, the suffix is the action. We read those plus the flat
// actor_name/entity fields straight off the row.
// ---------------------------------------------------------------------------

import type { NotificationView } from '@/database/services/NotificationService'
import { formatStatus } from '@/utils/badgeHelpers'

// Maps a dotted-type prefix to the URL segment + display noun for that entity.
// `article` is rendered plain, `noun` is bolded — "a <strong>bug</strong>".
const ENTITY_META: Record<string, { segment: string; article: string; noun: string }> = {
  bug: { segment: 'bugs', article: 'a', noun: 'bug' },
  feature: { segment: 'features', article: 'a', noun: 'feature' },
  testcase: { segment: 'test-cases', article: 'a', noun: 'test case' },
  testrun: { segment: 'test-runs', article: 'a', noun: 'test run' },
  accessibility: { segment: 'accessibility', article: 'an', noun: 'accessibility issue' },
}

function entityPrefix(n: NotificationView): string {
  return n.type.split('.')[0]
}

// A notification sentence, split into segments so the actor + entity can be
// rendered bold while the connective words stay regular. The sentence refers to
// the entity by its generic noun ("a bug"); the specific title is surfaced
// separately via notificationTitle().
export interface MessageSegment {
  text: string
  bold?: boolean
}

const plain = (text: string): MessageSegment => ({ text })
const strong = (text: string): MessageSegment => ({ text, bold: true })

// Generic noun for the entity as segments — plain article + bold noun,
// e.g. "a " + "bug". Falls back to "an item".
function entityNoun(n: NotificationView): MessageSegment[] {
  const meta = ENTITY_META[entityPrefix(n)]
  if (!meta) return [plain('an '), strong('item')]
  return [plain(`${meta.article} `), strong(meta.noun)]
}

// Old/new status carried in metadata for *.status_changed notifications
// (backend writes metadata.status = { old, new }).
function statusChange(n: NotificationView): { old?: string; new?: string } | null {
  const s = n.metadata?.status
  return s && typeof s === 'object' ? s : null
}

export function notificationMessageSegments(n: NotificationView): MessageSegment[] {
  const who = strong(n.actor_name ?? 'Someone')
  const what = entityNoun(n)

  switch (n.type) {
    case 'bug.assigned':
    case 'testcase.assigned':
    case 'accessibility.assigned':
      return [who, plain(' assigned '), ...what, plain(' to you')]
    case 'feature.qa_assigned':
      return [who, plain(' assigned you as a '), strong('QA'), plain(' to '), ...what]
    case 'feature.dev_assigned':
      return [who, plain(' assigned you as a '), strong('developer'), plain(' to '), ...what]
    case 'bug.status_changed':
    case 'feature.status_changed':
    case 'accessibility.status_changed':
    case 'testrun.status_changed': {
      const status = statusChange(n)
      // When we know the transition, spell it out: "… changed a bug status from
      // <old> to <new>". Otherwise fall back to the generic phrasing.
      if (status?.old && status?.new) {
        // metadata.status carries raw backend enum values (e.g. "in_progress");
        // humanize them so the sentence reads "from Open to In Progress".
        return [who, plain(' changed '), ...what, plain(' status from '), strong(formatStatus(status.old)), plain(' to '), strong(formatStatus(status.new))]
      }
      return [who, plain(' updated the status of '), ...what]
    }
    case 'bug.created':
    case 'feature.created':
      return [who, plain(' created '), ...what]
    case 'testrun.completed':
      return [who, plain(' completed '), ...what]
    case 'system':
      return [strong('System notification')]
    default:
      return [who, plain(' updated '), ...what]
  }
}

// Flat string form (e.g. for aria-labels / titles).
export function notificationMessage(n: NotificationView): string {
  return notificationMessageSegments(n)
    .map(s => s.text)
    .join('')
}

// The specific entity's title for the muted preview line under the message,
// e.g. "timepicker unable to change am/pm". Null when there's nothing to show
// (e.g. system notices, or an entity with no title).
export function notificationTitle(n: NotificationView): string | null {
  return n.entity_title ?? null
}

export function notificationRoute(n: NotificationView): string | null {
  const prefix = entityPrefix(n)
  const meta = ENTITY_META[prefix]
  if (!meta) return null
  // Accessibility has no per-issue detail page (only the list view), so route to
  // the project's accessibility page rather than a non-existent /accessibility/:id.
  // When the notification carries no project, fall back to the main accessibility page.
  if (prefix === 'accessibility') {
    return n.project_id ? `/projects/${n.project_id}/${meta.segment}?view=detailed` : '/accessibility?view=detailed'
  }
  // Other entities: route on the human-readable business id (B00001…) — detail
  // pages resolve by it, matching the existing assigned-to-me links.
  if (!n.project_id || !n.entity_id) return null
  return `/projects/${n.project_id}/${meta.segment}/${n.entity_id}`
}
